import { useCallback } from 'react';
import { extend, get, isEmpty } from 'lodash';
import { unpackValidationErrors } from '../../../../utilities/FormUtilities';
import { HTTP_STATUS_400, HTTP_STATUS_500 } from '../../../../services/ApiDataService';

/**
 * Extract hostname from error message
 * @param {string} message - Error message
 * @returns {string|null} Extracted hostname or null
 */
export const extractHostname = (message) => {
    const hostnamePattern = /hostname\s+(\S+)\s+cannot\s+be\s+resolved/i;
    const match = message.match(hostnamePattern);
    return match ? match[1] : null;
};

/**
 * Custom hook for handling infrastructure save errors
 * Provides consistent error handling for hostname/IP validation across infrastructure components
 *
 * @returns {Function} Error handler function
 */
export function useInfrastructureSaveError() {
    /**
     * Handle save errors with specific logic for hostname/IP validation
     * @param {Object} errorObject - Error object from API
     * @throws {Object} Extended error object with responseErrorMessage
     */
    const handleSaveError = useCallback((errorObject) => {
        let responseErrorMessage;

        if (errorObject.getStatus) {
            if (errorObject.getStatus() === HTTP_STATUS_400) {
                const validationErrors = unpackValidationErrors(errorObject.responseBody);

                if (typeof validationErrors === 'string') {
                    if (validationErrors === 'caller_and_or_callee_not_supplied') {
                        responseErrorMessage = 'A Caller and a Callee must be selected.';
                    }
                } else if (typeof validationErrors === 'object' && validationErrors !== null) {
                    if (validationErrors.ipAddress) {
                        if (validationErrors.ipAddress.message === 'Value must be unique') {
                            responseErrorMessage =
                                'Hostname or IP address of the hostname with same port number already exists.';
                        }
                    }

                    if (validationErrors.hostname) {
                        if (validationErrors.hostname.message === 'Value must be unique') {
                            responseErrorMessage =
                                'Hostname or IP address of the hostname with same port number already exists.';
                        }

                        if (validationErrors.hostname.message === 'The hostname cannot be resolved') {
                            /**
                             *  if (response.errno === 'ENOTFOUND') {
                             *      throw new HttpErrors.HttpBadRequestError(constants.CUSTOM_ERRORS.HOSTNAME_CANNOT_RESOLVED);
                             *   }
                             */
                            responseErrorMessage = 'The hostname cannot be resolved.';
                        }
                    }
                }
            } else if (errorObject.getStatus() === HTTP_STATUS_500) {
                const message = get(errorObject.responseBody, 'message');
                if (!isEmpty(message)) {
                    const hostname = extractHostname(message);
                    if (hostname) {
                        responseErrorMessage = `The hostname ${hostname} cannot be resolved`;
                    } else {
                        responseErrorMessage = message;
                    }
                }
            }
        }

        throw extend(errorObject, {
            responseErrorMessage,
        });
    }, []);

    return handleSaveError;
}
